import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute - Wrapper component for routes that require authentication
 * @param {ReactNode} children - The component to render if authenticated
 * @param {Array<string>} allowedRoles - Array of roles allowed to access this route (optional)
 * @param {string} redirectTo - Where to redirect if not authenticated (default: '/login')
 */
const ProtectedRoute = ({ children, allowedRoles = null, redirectTo = '/login' }) => {
  const { isAuthenticated, loading, userRole } = useAuth();
  const location = useLocation();

  // ✅ Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
        backgroundColor: '#f3f2ef'
      }}>
        <div style={{ textAlign: 'center' }}>
          {/* Spinning loader */}
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e1e8ed',
            borderTop: '4px solid #0a66c2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ 
            color: '#666', 
            fontSize: '16px',
            fontWeight: '500',
            margin: '0'
          }}>
            Loading...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ✅ Check if user is authenticated
  if (!isAuthenticated) {
    console.log('🚫 ProtectedRoute: Not authenticated - redirecting to', redirectTo);
    console.log('- Attempted to access:', location.pathname);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // ✅ Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAccess = allowedRoles.includes(userRole);
    
    console.log('🔐 ProtectedRoute: Role-based access check:', {
      userRole,
      allowedRoles,
      hasAccess,
      path: location.pathname
    });

    if (!hasAccess) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
          backgroundColor: '#f3f2ef'
        }}>
          <div style={{
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            padding: '48px 32px',
            border: '1px solid #e1e8ed',
            borderRadius: '16px',
            backgroundColor: '#fff',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
          }}>
            {/* Denied Icon */}
            <div style={{
              fontSize: '64px',
              marginBottom: '20px',
              lineHeight: '1'
            }}>⛔</div>
            
            {/* Title */}
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#000',
              letterSpacing: '-0.5px'
            }}>Access Denied</h2>
            
            {/* Description */}
            <p style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              You don't have permission to view this page. Please contact your administrator if you believe this is an error.
            </p>
            
            {/* Role Info Box */}
            <div style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              marginBottom: '24px',
              border: '1px solid #e1e8ed'
            }}>
              <div style={{ 
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e1e8ed'
              }}>
                <p style={{ 
                  margin: '0', 
                  fontSize: '13px', 
                  color: '#999',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Your Role
                </p>
                <p style={{ 
                  margin: '4px 0 0 0', 
                  fontSize: '16px', 
                  color: '#000',
                  fontWeight: '600'
                }}>
                  {userRole || 'Unknown'}
                </p>
              </div>
              <div>
                <p style={{ 
                  margin: '0', 
                  fontSize: '13px', 
                  color: '#999',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Required Roles
                </p>
                <p style={{ 
                  margin: '4px 0 0 0', 
                  fontSize: '16px', 
                  color: '#000',
                  fontWeight: '600'
                }}>
                  {allowedRoles.join(', ')}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => window.history.back()}
                style={{
                  padding: '12px 28px',
                  backgroundColor: '#fff',
                  color: '#0a66c2',
                  border: '1px solid #0a66c2',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f3f2ef';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#fff';
                }}
              >
                Go Back
              </button>
              <button
                onClick={() => window.location.href = '/dashboard'}
                style={{
                  padding: '12px 28px',
                  backgroundColor: '#0a66c2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#004182';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#0a66c2';
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ✅ User is authenticated and has proper role - render the protected content
  console.log('✅ ProtectedRoute: Access granted to', location.pathname);
  return children;
};

// ==========================================
// Role-Specific Route Wrappers
// ==========================================

/**
 * CandidateRoute - Only CANDIDATE and ADMIN can access
 */
export const CandidateRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['CANDIDATE', 'ADMIN']}>
    {children}
  </ProtectedRoute>
);

/**
 * EmployerRoute - Only EMPLOYER and ADMIN can access
 */
export const EmployerRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['EMPLOYER', 'ADMIN']}>
    {children}
  </ProtectedRoute>
);

/**
 * AgencyRoute - Only AGENCY and ADMIN can access
 */
export const AgencyRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['AGENCY', 'ADMIN']}>
    {children}
  </ProtectedRoute>
);

/**
 * AdminRoute - Only ADMIN can access
 */
export const AdminRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={['ADMIN']}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
